/*
 *  Copyright (c) 2022-2023 Nordcloud Oy or its affiliates. All Rights Reserved.
 */

import { useMemo } from "react";
import { ParentSize } from "@visx/responsive";
import * as queryString from "query-string";
import { When } from "react-if";
import { generatePath } from "react-router-dom";
import { Spacer, theme } from "@nordcloud/gnui";
import { BrickLoader, UniversalWrap } from "~/components";
import {
  getColorsPalette,
  usePeriod,
  getTableColumns,
  BudgetSetupNotification,
  CostVsBudgetChart,
} from "~/components/Charts/CostAnalysis";
import {
  ChartType,
  Granularity,
  Period,
} from "~/components/Charts/CostAnalysis/types";
import { useCurrency } from "~/hooks";
import { ROUTES } from "~/routing/routes";
import { ApplicationCostTableData } from "../ApplicationCostTableData";
import {
  useApplicationAccumulatedCostChart,
  useCostVersusBudgetChart,
} from "../hooks";

type Props = {
  appId: string;
  startDate: string;
  endDate: string;
  granularity: Granularity;
  period: Period;
  hasFooter: boolean;
  showCostTable: boolean;
  forecast: number;
};

export function ApplicationCostVersusBudgetView({
  appId,
  startDate,
  endDate,
  granularity,
  period,
  hasFooter,
  showCostTable,
  forecast,
}: Props) {
  const { currency } = useCurrency();
  const { currentYear } = usePeriod();

  const {
    accumulatedCostTotal,
    accumulatedCostTimePoints,
    accumulatedCostBudgetChartData,
    loading: isAccumulatedCostLoading,
    error: accumulatedCostError,
  } = useApplicationAccumulatedCostChart({
    appId,
    startDate,
    endDate,
    period,
    granularity,
  });

  const {
    costVersusBudgetChartData,
    applicationBudgetTable,
    applicationBudgetData,
    applicationBudgetTotal,
    hasBudget,
    loading: isBudgetLoading,
    error: budgetError,
  } = useCostVersusBudgetChart({
    applicationId: appId,
    currentYear,
    period,
    accumulatedCostBudgetChartData,
    startDate,
    endDate,
    accumulatedCostTimePoints,
    accumulatedCostTotal,
  });

  const columns = useMemo(
    () =>
      getTableColumns({
        startDate,
        endDate,
        currency,
        data: applicationBudgetTable,
        hasFooter,
        period,
        chartType: ChartType.COST_VS_BUDGET,
        colorsPalette: getColorsPalette(ChartType.COST_VS_BUDGET),
      }),
    [currency, startDate, endDate, hasFooter, applicationBudgetTable]
  );

  const isLoading = isAccumulatedCostLoading || isBudgetLoading;
  const error = accumulatedCostError || budgetError;

  return (
    <>
      <UniversalWrap
        loaderProps={{ loading: isLoading, Component: BrickLoader }}
        errorProps={{ error }}
      >
        <When condition={!hasBudget}>
          <Spacer height={theme.spacing.spacing02} />
          <BudgetSetupNotification
            link={`${generatePath(ROUTES.applications.details, {
              application: appId,
            })}?${queryString.stringify({ costTab: "budget" })}`}
          />
        </When>
        <ParentSize>
          {(parent) => (
            <CostVsBudgetChart
              width={parent.width}
              height={340}
              loading={isLoading}
              data={costVersusBudgetChartData}
              granularity={granularity}
              startDate={startDate}
              endDate={endDate}
              budgetData={applicationBudgetData}
              forecastValue={forecast}
            />
          )}
        </ParentSize>
        <ApplicationCostTableData
          period={period}
          startDate={startDate}
          endDate={endDate}
          tableData={applicationBudgetTable}
          chartType={ChartType.COST_VS_BUDGET}
          total={applicationBudgetTotal}
          tableError={error}
          columns={columns}
          isLoading={isLoading}
          showCostTable={showCostTable}
          hasFooter={hasFooter}
        />
      </UniversalWrap>
    </>
  );
}
